import {z} from 'zod';

import {BaseVoidResponseSchema, CreatedAtAndUpdatedBySchema} from '@dms/api/shared';

export const ArticleUpdateJobStateSchema = z.enum([
  'NEW',
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
]);
export type ArticleUpdateJobState = z.infer<typeof ArticleUpdateJobStateSchema>;

export const ArticleUpdateJobSourceSchema = z.enum(['USER', 'INTEGRATION']);
export type ArticleUpdateJobSource = z.infer<typeof ArticleUpdateJobSourceSchema>;

export const ArticleScopeArticleDefaultSupplierSchema = z.enum(['ANY', 'ONLY']);

export const UpdateScopeSupplierArticleSchema = z.enum([
  'RECOMMENDED_PRICE',
  'PURCHASE_PRICE',
  'SUPPLIER_DISCOUNT_GROUP',
  'BULK_PACKAGE_QUANTITY',
  'ORDERING_NUMBER',
]);
export type UpdateScopeSupplierArticle = z.infer<typeof UpdateScopeSupplierArticleSchema>;
export const UpdateScopeWarehouseArticleSchema = z.enum([
  'RECOMMENDED_PRICE',
  'NAME',
  'SUPPLIER_DISCOUNT_GROUP',
]);
export type UpdateScopeWarehouseArticle = z.infer<typeof UpdateScopeWarehouseArticleSchema>;

/**
 * GET
 */
export const GetArticleUpdateJobRequestSchema = z.object({
  articleUpdateJobId: z.string(),
});
export type GetArticleUpdateJobRequest = z.infer<typeof GetArticleUpdateJobRequestSchema>;

export const GetArticleUpdateJobResponseSchema = z
  .object({
    articleUpdateJobId: z.string(),
    branchId: z.string(),
    state: ArticleUpdateJobStateSchema,
    source: ArticleUpdateJobSourceSchema,
    note: z.string().nullish(),
    supplierId: z.string(),
    publishedAt: z.string().nullish(),
    isApproved: z.boolean(),
    approvedAt: z.string().nullish(),
    approvedBy: z.string().nullish(),
    plannedExecutionAt: z.string().nullish(),
    plannedBy: z.string().nullish(),
    isPlanned: z.boolean(),
    executedAt: z.string().nullish(),
    completedAt: z.string().nullish(),
    canceledAt: z.string().nullish(),
    canceledBy: z.string().nullish(),
    articleScopeLimitToUpdateList: z.boolean(),
    articleScopeBranchAll: z.boolean(),
    articleScopeBranches: z.array(z.string()).nullish(),
    articleScopeWarehouseAll: z.boolean(),
    articleScopeWarehouses: z.array(z.string()).nullish(),
    articleScopeArticleDefaultSupplier: ArticleScopeArticleDefaultSupplierSchema,
    updateScopeSupplierArticleAll: z.boolean(),
    updateScopeSupplierArticle: z.array(UpdateScopeSupplierArticleSchema).nullish(),
    updateScopeWarehouseArticleAll: z.boolean(),
    updateScopeWarehouseArticle: z.array(UpdateScopeWarehouseArticleSchema).nullish(),
    integrationUpdateListId: z.string().nullish(),
  })
  .merge(CreatedAtAndUpdatedBySchema);
export type GetArticleUpdateJobResponse = z.infer<typeof GetArticleUpdateJobResponseSchema>;

/**
 * POST
 */
export const PostArticleUpdateJobRequestSchema = z.object({
  branchId: z.string(),
  source: ArticleUpdateJobSourceSchema,
  supplierId: z.string(),
  note: z.string().nullish(),
  articleScopeLimitToUpdateList: z.boolean(),
  articleScopeBranchAll: z.boolean(),
  articleScopeBranches: z.array(z.string()).nullish(),
  articleScopeWarehouseAll: z.boolean(),
  articleScopeWarehouses: z.array(z.string()).nullish(),
  articleScopeArticleDefaultSupplier: ArticleScopeArticleDefaultSupplierSchema,
  updateScopeSupplierArticleAll: z.boolean(),
  updateScopeSupplierArticle: z.array(UpdateScopeSupplierArticleSchema).nullish(),
  updateScopeWarehouseArticleAll: z.boolean(),
  updateScopeWarehouseArticle: z.array(UpdateScopeWarehouseArticleSchema).nullish(),
  integrationUpdateListId: z.string().nullish(),
});
export type PostArticleUpdateJobRequest = z.infer<typeof PostArticleUpdateJobRequestSchema>;

export const PostArticleUpdateJobResponseSchema = z.object({
  articleUpdateJobId: z.string(),
});
export type PostArticleUpdateJobResponse = z.infer<typeof PostArticleUpdateJobResponseSchema>;

/**
 * PATCH
 */
export const PatchArticleUpdateJobRequestSchema = z.object({
  articleUpdateJobId: z.string(),
  body: z.object({
    note: z.string().nullish(),
    supplierId: z.string(),
    articleScopeLimitToUpdateList: z.boolean(),
    articleScopeBranchAll: z.boolean(),
    articleScopeBranches: z.array(z.string()).nullish(),
    articleScopeWarehouseAll: z.boolean(),
    articleScopeWarehouses: z.array(z.string()).nullish(),
    articleScopeArticleDefaultSupplier: ArticleScopeArticleDefaultSupplierSchema,
    updateScopeSupplierArticleAll: z.boolean(),
    updateScopeSupplierArticle: z.array(UpdateScopeSupplierArticleSchema).nullish(),
    updateScopeWarehouseArticleAll: z.boolean(),
    updateScopeWarehouseArticle: z.array(UpdateScopeWarehouseArticleSchema).nullish(),
  }),
});
export type PatchArticleUpdateJobRequest = z.infer<typeof PatchArticleUpdateJobRequestSchema>;

export const PatchArticleUpdateJobResponseSchema = BaseVoidResponseSchema;
export type PatchArticleUpdateJobResponse = z.infer<typeof PatchArticleUpdateJobResponseSchema>;

/**
 * DELETE
 */
export const DeleteArticleUpdateJobRequestSchema = z.object({
  articleUpdateJobId: z.array(z.string()),
});
export type DeleteArticleUpdateJobRequest = z.infer<typeof DeleteArticleUpdateJobRequestSchema>;

export const DeleteArticleUpdateJobResponseSchema = BaseVoidResponseSchema;
export type DeleteArticleUpdateJobResponse = z.infer<typeof DeleteArticleUpdateJobResponseSchema>;

/**
 * PUT
 */
